import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StickyNote, Loader2, Bug, Lightbulb, CheckSquare, Sparkles, Search, Filter, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import FeedbackDetailModal from "./FeedbackDetailModal";
import { BUTLER_STATUS_META } from "./feedbackStatus";
import { useF1Help } from "@/lib/useF1Help";

const TYP_META = {
  bug: { label: "Bug", icon: Bug, color: "bg-red-100 text-red-700" },
  feature: { label: "Feature", icon: Sparkles, color: "bg-violet-100 text-violet-700" },
  idee: { label: "Idee", icon: Lightbulb, color: "bg-amber-100 text-amber-700" },
  test: { label: "Test", icon: CheckSquare, color: "bg-sky-100 text-sky-700" },
};

const PRIO_META = {
  hoch: { dot: "bg-red-500", label: "Hoch" },
  normal: { dot: "bg-amber-400", label: "Normal" },
  niedrig: { dot: "bg-emerald-500", label: "Niedrig" },
};

const STATUS_META = {
  offen: { label: "Offen", color: "bg-blue-100 text-blue-800" },
  in_arbeit: { label: "In Arbeit", color: "bg-amber-100 text-amber-800" },
  erledigt: { label: "Erledigt", color: "bg-emerald-100 text-emerald-800" },
};

const ZU_PRUEFEN = ["behoben", "ueberprueft"];  // wartet auf Ralphs Prüfung im Betrieb
const zuPruefen = (it) => ZU_PRUEFEN.includes(it.butler?.status);
// Eine Regel für Karten UND Filter (Ralph 24.10.2026): jede Zeile gehört genau einer Gruppe.
const gruppe = (it) => (zuPruefen(it) ? "zu_pruefen" : it.status);
const hochOffen = (it) => it.prio === "hoch" && ["offen", "in_arbeit"].includes(gruppe(it));
const statusMeta = (it) => BUTLER_STATUS_META[it.butler?.status] || STATUS_META[it.status] || STATUS_META.offen;

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

const ModuleFeedbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [historyCounts, setHistoryCounts] = useState({});  // {feedback_id: count}
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("alle");
  const [typFilter, setTypFilter] = useState("alle");
  const [prioFilter, setPrioFilter] = useState("alle");
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  useF1Help("hilfe_notizen_bugs");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const inc = includeArchived ? "&include_archived=true" : "";
      const r = await api.get(`/module-feedback/list?status=alle&typ=alle${inc}&limit=500`);
      setItems(r.data || []);
      // Bemerkungen-Zähler asynchron laden (klein, akzeptabel)
      const counts = {};
      await Promise.all((r.data || []).map(async (it) => {
        try {
          const h = await api.get(`/module-feedback/${it.id}/history`);
          counts[it.id] = (h.data || []).filter((e) => e.type === "kommentar").length;
        } catch { counts[it.id] = 0; }
      }));
      setHistoryCounts(counts);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  // ?open={id} → Datensatz direkt im Detail-Modal öffnen.
  // Kommt vom Floating-Widget oder externer Verlinkung.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (!openId || items.length === 0) return;
    const target = items.find((it) => it.id === openId);
    if (target) {
      setSelected(target);
      // URL aufräumen, damit Refresh nicht erneut triggert
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete("open");
      navigate(`${location.pathname}${cleaned.toString() ? "?" + cleaned.toString() : ""}`, { replace: true });
    }
  }, [location.search, items, navigate, location.pathname]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter === "hoch_offen" ? !hochOffen(it) : statusFilter !== "alle" && gruppe(it) !== statusFilter) return false;
      if (typFilter !== "alle" && it.typ !== typFilter) return false;
      if (prioFilter !== "alle" && it.prio !== prioFilter) return false;
      if (q) {
        const hay = `${it.title} ${it.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, statusFilter, typFilter, prioFilter, search]);

  const stats = useMemo(() => {
    const s = { total: items.length, offen: 0, in_arbeit: 0, erledigt: 0, hoch: 0, zu_pruefen: 0 };
    items.forEach((it) => {
      if (gruppe(it) in s) s[gruppe(it)] += 1;
      if (hochOffen(it)) s.hoch += 1;
    });
    return s;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="module-feedback-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <StickyNote className="w-6 h-6" /> Notizen & Bugs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Persönliche Notizen, gemeldete Bugs, Ideen und Tests – mit komplettem Verlauf pro Eintrag.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="btn-feedback-page-new"
        >
          <Plus className="w-4 h-4" /> Neuer Eintrag
        </button>
      </div>

      {/* Statistik-Karten: antippen = nur diese zeigen, nochmal antippen = alle */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-sm">
        {[
          ["alle", "Gesamt", stats.total, "bg-card", "text-muted-foreground", "", "ring-slate-400"],
          ["offen", "Offen", stats.offen, "bg-blue-50 border-blue-200", "text-blue-800", "text-blue-900", "ring-blue-400"],
          ["in_arbeit", "In Arbeit", stats.in_arbeit, "bg-amber-50 border-amber-200", "text-amber-800", "text-amber-900", "ring-amber-400"],
          ["zu_pruefen", "Bitte prüfen", stats.zu_pruefen, "bg-violet-50 border-violet-200", "text-violet-800", "text-violet-900", "ring-violet-400"],
          ["erledigt", "Erledigt", stats.erledigt, "bg-emerald-50 border-emerald-200", "text-emerald-800", "text-emerald-900", "ring-emerald-400"],
          ["hoch_offen", "Hohe Prio offen", stats.hoch, "bg-red-50 border-red-200", "text-red-800", "text-red-900", "ring-red-400"],
        ].map(([wert, titel, zahl, farbe, schrift, zahlFarbe, ring]) => (
          <button
            key={wert}
            type="button"
            onClick={() => setStatusFilter(statusFilter === wert ? "alle" : wert)}
            className={`text-left border rounded-sm p-3 hover:brightness-95 ${farbe} ${statusFilter === wert && wert !== "alle" ? `ring-2 ${ring}` : ""}`}
            title={wert === "zu_pruefen" ? "Behoben, wartet auf Ihre Prüfung im Betrieb – antippen zeigt nur diese" : `Antippen zeigt nur „${titel}“`}
            data-testid={wert === "zu_pruefen" ? "karte-zu-pruefen" : `karte-${wert}`}
          >
            <div className={`text-xs ${schrift}`}>{titel}</div>
            <div className={`text-2xl font-bold ${zahlFarbe}`}>{zahl}</div>
          </button>
        ))}
      </div>

      {/* Filter */}
      <div className="border rounded-sm p-3 bg-muted/30 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filter
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche in Titel / Beschreibung…"
              className="w-full pl-8 pr-3 py-2 text-sm border rounded-sm bg-background"
              data-testid="input-feedback-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border rounded-sm bg-background"
            data-testid="select-feedback-status"
          >
            <option value="alle">Alle Status</option>
            <option value="offen">Offen</option>
            <option value="in_arbeit">In Arbeit</option>
            <option value="zu_pruefen">Bitte prüfen (behoben)</option>
            <option value="erledigt">Erledigt</option>
            <option value="hoch_offen">Hohe Prio offen</option>
          </select>
          <select
            value={typFilter}
            onChange={(e) => setTypFilter(e.target.value)}
            className="px-3 py-2 text-sm border rounded-sm bg-background"
            data-testid="select-feedback-typ"
          >
            <option value="alle">Alle Typen</option>
            {Object.entries(TYP_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
          <select
            value={prioFilter}
            onChange={(e) => setPrioFilter(e.target.value)}
            className="px-3 py-2 text-sm border rounded-sm bg-background"
            data-testid="select-feedback-prio"
          >
            <option value="alle">Alle Prios</option>
            {Object.entries(PRIO_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="checkbox-feedback-archive"
            />
            Archiv (älter als 30 Tage) einschließen
          </label>
        </div>
      </div>

      {/* Tabelle */}
      <div className="border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Titel</th>
                <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Typ</th>
                <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Prio</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Erstellt</th>
                <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Geändert</th>
                <th className="text-center px-3 py-2 font-semibold hidden md:table-cell">Bemerkungen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Lade…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Keine Einträge mit diesen Filtern.
                </td></tr>
              ) : filtered.map((it) => {
                const meta = TYP_META[it.typ] || TYP_META.bug;
                const TypIcon = meta.icon;
                const prio = PRIO_META[it.prio] || PRIO_META.normal;
                const status = statusMeta(it);
                const cnt = historyCounts[it.id] || 0;
                return (
                  <tr
                    key={it.id}
                    onClick={() => setSelected(it)}
                    className="border-b hover:bg-accent/30 cursor-pointer"
                    data-testid={`feedback-row-${it.id}`}
                  >
                    <td className="px-3 py-2 max-w-[420px]">
                      <div className={`font-medium ${it.status === "erledigt" ? "line-through text-muted-foreground" : ""}`}>
                        {it.title}
                      </div>
                      {it.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">{it.description}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-sm ${meta.color} font-medium`}>
                        <TypIcon className="w-2.5 h-2.5" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <span className={`w-2 h-2 rounded-full ${prio.dot}`} /> {prio.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium whitespace-nowrap ${status.color}`} data-testid={`feedback-status-${it.id}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                      {fmt(it.created_at)}
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                      {fmt(it.updated_at)}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-center text-xs">
                      {cnt > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary/10 text-primary font-semibold">
                          {cnt}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail/Edit Modal */}
      {selected && (
        <FeedbackDetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => { await load(); }}
          onDeleted={async () => { setSelected(null); await load(); }}
        />
      )}

      {/* Create Modal */}
      {showCreate && (
        <FeedbackDetailModal
          item={null}
          onClose={() => setShowCreate(false)}
          onChanged={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </div>
  );
};

export default ModuleFeedbackPage;
